import * as React from 'react';
import { RadioGroup } from '@progress/kendo-react-inputs';
import styled from 'styled-components';
import { useController, useFormContext } from 'react-hook-form';
import { FieldProps, generateValidations } from '../../core/form';
import { QuestionOption } from '../../models/QuestionModels';
import { otherFieldName } from '../../core/constants';
import { slateGrey, tabletPortraitMinWidth } from '../../appComponents/cssConstants';
import Input from '../appForm/Input';

const RadioGroupQuestion = (props: FieldProps) => {
	const { name, data } = props;
	const { setValue } = useFormContext();
	const { field } = useController({ name, rules: generateValidations(props) });
	const { value } = field;

	const handleChange = (e: any) => {
		field.onChange(e.value);
		if (props.onChange) props.onChange(e);
	};

	return (
		<>
			<StyledRadio
				name={name}
				value={value}
				className="radioGroup"
				data={setOtherOptionValue(value, data)}
				onChange={handleChange}
			/>
			{showOtherInput(value, data) && (
				<StyledInput
					label="Other (Please specify)"
					name={otherFieldName}
					required
					onBlur={(e: any) => setValue(otherFieldName, e.target.value)}
				/>
			)}
		</>
	);
};

export default RadioGroupQuestion;

const showOtherInput = (fieldValue: string, data: QuestionOption<string | number>[]) =>
	!!fieldValue && !data.filter((x) => !x.showOtherTextField).some((x) => x.value === fieldValue);

const setOtherOptionValue = (fieldValue: string, data: QuestionOption<string | number>[]) => {
	if (!fieldValue)
		return data.map((x) => {
			const { value, label } = x; // To remove other custom props which causes React warning of passing unknown prop to DOM.
			return { value, label };
		});

	const newData = data.map((x) => {
		if (x.showOtherTextField && showOtherInput(fieldValue, data)) {
			// Set the fieldValue to Option value, so that the 'Other' radio option will be checked and highlighted.
			return { value: fieldValue, label: x.label };
		}
		const { value, label } = x; // To remove other custom props which causes React warning of passing unknown prop to DOM.
		return { value, label };
	});
	return newData;
};

const StyledRadio = styled(RadioGroup)`
	display: flex;
	align-items: stretch;
	flex-flow: row wrap !important; // To override kendo styling

	li {
		display: inline-block;
		width: 100%;

		@media (min-width: ${tabletPortraitMinWidth}) {
			width: calc(50% - 10px);

			&:nth-child(odd) {
				margin-right: 20px;
			}
		}

		.k-radio-wrap {
			display: none;
		}

		.k-radio-label {
			width: 100%;
			height: 100%;
			margin: 0;
			padding: 16px 24px;
			font-size: 16px;
			line-height: 19px;
			color: ${slateGrey};
			background: #fff;
			border: 1px solid #e5e5e5;
			border-radius: 26px;
			display: flex;
			align-items: center;
		}

		.k-radio-wrap:has(.k-radio.k-checked) + .k-radio-label {
			color: #fff;
			background: ${(props) => props.theme.brandingColourHeavy};
			border-color: ${(props) => props.theme.brandingColourHeavy};
		}
	}
`;

const StyledInput = styled(Input)`
	margin-top: 4px; // This input is outside of RadioGroup ul, so need margin-top to add gap
`;
